import { SignedIn, SignedOut, UserButton, useUser } from '@clerk/clerk-react';
import { useHealth, useMe } from '@studio/api-client';
import { Button, Chip, TopBar } from '@studio/ui';
import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { ThemeToggle } from '../components/ThemeToggle';

export function HomePage() {
  const health = useHealth();

  return (
    <div className="flex min-h-dvh flex-col">
      <TopBar
        left={<Logo />}
        right={
          <>
            <HealthBadge ok={health.data?.ok === true} pending={health.isPending} />
            <SignedOut>
              <Button asChild variant="secondary" size="sm">
                <Link to="/sign-in">Sign in</Link>
              </Button>
            </SignedOut>
            <ThemeToggle />
            <SignedIn>
              <UserButton afterSignOutUrl="/" />
            </SignedIn>
          </>
        }
      />
      <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col items-center justify-center px-6 py-12 text-center">
        <p className="eyebrow mb-4">Cross-platform AI media hub</p>
        <h1 className="display display-2xl mx-auto max-w-[20ch] text-display-2xl">
          One AI studio for every store you sell on.
        </h1>
        <p className="mt-5 max-w-[52ch] text-md text-fg-2">
          Generate product photography and social-ready visuals once. Publish them to Etsy, Wix,
          eBay and more — all from one cross-platform media hub.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <SignedOut>
            <Button asChild variant="primary">
              <Link to="/sign-up">Get started</Link>
            </Button>
            <Button asChild variant="secondary">
              <Link to="/sign-in">Sign in</Link>
            </Button>
          </SignedOut>
          <SignedIn>
            <Button asChild variant="primary">
              <Link to="/products">Browse products</Link>
            </Button>
            <Button asChild variant="secondary">
              <Link to="/gallery">Open gallery</Link>
            </Button>
          </SignedIn>
          {import.meta.env.VITE_ENABLE_DEV_ROUTE === 'true' ? (
            <Button asChild variant="ghost">
              <Link to="/dev">Dev center</Link>
            </Button>
          ) : null}
        </div>
        <SignedIn>
          <SessionDetails />
        </SignedIn>
      </main>
    </div>
  );
}

function SessionDetails() {
  const { user } = useUser();
  const me = useMe();

  return (
    <div className="mt-12 grid w-full max-w-md gap-3 rounded-lg border border-border bg-surface p-5 text-left text-sm">
      <p className="text-fg-3">Signed in as</p>
      <p className="font-medium text-fg">
        {user?.primaryEmailAddress?.emailAddress ?? user?.id ?? '—'}
      </p>

      <div className="border-t border-border-subtle pt-3 text-xs text-fg-3">
        <p className="mb-1">
          Round-trip from <code className="font-mono">/api/me</code>:
        </p>
        {me.isPending ? (
          <p>Loading…</p>
        ) : me.error ? (
          <p className="text-danger">Error: {(me.error as Error).message}</p>
        ) : me.data ? (
          <>
            <p>
              <span className="text-fg-3">userId:</span>{' '}
              <code className="font-mono text-fg-2">{me.data.userId}</code>
            </p>
            <p>
              <span className="text-fg-3">workspaceId:</span>{' '}
              <code className="font-mono text-fg-2">
                {me.data.workspaceId ?? <em>(not set — Clerk JWT template needed)</em>}
              </code>
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}

interface HealthBadgeProps {
  ok: boolean;
  pending: boolean;
}

function HealthBadge({ ok, pending }: HealthBadgeProps) {
  if (pending) {
    return (
      <Chip variant="soft" tone="neutral">
        checking…
      </Chip>
    );
  }
  if (ok) {
    return (
      <Chip variant="soft" tone="success">
        api up
      </Chip>
    );
  }
  return (
    <Chip variant="soft" tone="danger">
      api down
    </Chip>
  );
}
